import { createSelector, createSlice } from "@reduxjs/toolkit";
import { logout } from "../../common/reducers/initContext";
import { initMapFactory, queryMapFactory, DefaultFailureAction, DefaultSuccessAction, initMultiMapFactory, dataHookWithCredentialsFactory, readonlyHookWithCredentialsFactory } from "../../common/reducers/utilities";
import { aggregatingFetch } from "../../common/hooks/networkHook";
import { dateRangeOverlap } from "../../common/utils/dateUtils";
import { ensureArray } from "../../common/utils/arrayUtilities";
import { ensureInteger } from "../../common/utils/generalUtils";
import { shiftIdString } from "../shift/shiftMgrSlice";
import { markFetched, registerEntityBasedFetch } from "../../common/hooks/initHook";

const key = shift => ensureInteger(shift.id);

const prepareArray = data => ensureArray(data).map(shift => ({
    ...shift,
    renderid: shiftIdString(shift)
}))

export const shiftClientSlice = createSlice({
    name: 'clientShifts',
    initialState: {
        data: {}
    },
    reducers: {
        set: (state, action) => {
            ensureArray(action.payload.ids).forEach(id => delete state.data[id]);
            prepareArray(action.payload.data).forEach(shift => state.data[key(shift)] = shift);
        },
        addOrUpdate: (state, action) => {
            prepareArray(action.payload).forEach(s => state.data[key(s)] = s);
        },
        remove: (state, action) => {
            ensureArray(action.payload).forEach(s => delete state.data[key(s)]);
        }
    },
    extraReducers: builder => {
        builder.addCase(logout, () => shiftClientSlice.getInitialState());
    }
});


// #region network operations
const visualizeSuccess = (verb, action) => DefaultSuccessAction(verb, 'shift_one', action);
const visualizeFailure = verb => DefaultFailureAction(verb, 'shift_one');

const fetchShift = aggregatingFetch(
    id => network => network.post('/api/v2/clientShifts', [id], data => shiftClientSlice.actions.set({ data, ids: [id] }), visualizeFailure('get')),
    ids => network => network.post('/api/v2/clientShifts', ids, data => shiftClientSlice.actions.set({ data, ids: ensureArray(ids) }), visualizeFailure('get')),
    { primitiveKey: true }
);

const fetchClientShiftsInRange = (start, end) => network => network.get(`/api/v2/clientShifts/${encodeURIComponent(start)}/${encodeURIComponent(end)}`, shiftClientSlice.actions.addOrUpdate, visualizeFailure('get'));
// #endregion


// #region selectors
const selectShiftData = state => state.clientShifts.data;
const selectShift = id => createSelector([selectShiftData], shifts => shifts[id]);
const selectClientShifts = inputIds => {
    const ids = ensureArray(inputIds);
    return createSelector([selectShiftData], shifts => ids.reduce((res, id) => {
        if (shifts[id]) {
            res.push(shifts[id]);
        }
        return res;
    }, []));
}
const selectClientShiftsInRange = (start, end) => createSelector([selectShiftData], shifts => {
    let result = Object.values(shifts).filter(s => dateRangeOverlap(s, { start, end }));
    result.sort((a, b) => (new Date(a.start)).getTime() - (new Date(b.start)).getTime());
    return result;
});

const listen = {
    shiftClientCreated: (shift, { dispatch, websocket }) => {
        markFetched(websocket, 'shift', key(shift))
        dispatch(shiftClientSlice.actions.addOrUpdate(shift))
    },
    shiftClientUpdated: (shift, { dispatch }) => dispatch(shiftClientSlice.actions.addOrUpdate(shift)),
    shiftClientRemoved: (shift, { dispatch }) => dispatch(shiftClientSlice.actions.remove(shift))
}

// have to use the same factory for the single and multiple gets so that single and multiple deps are unified

const shiftInitByID = initMapFactory(fetchShift, listen, { name: 'clientShiftByID' });
registerEntityBasedFetch('shift', shiftInitByID);

const options = {
    cleanup: { root: shiftClientSlice.name, all: state => Object.values(selectShiftData(state)), removeAll: shiftClientSlice.actions.remove, key },
    condition: credentials => credentials?.hasRole('client roster viewer')
}

const optionsSingle = { ...options, defaultValue: null}

export const [useClientShifts] = dataHookWithCredentialsFactory(selectClientShifts, initMultiMapFactory(shiftInitByID, listen), options);
export const [useClientShift, realizeClientShift] = dataHookWithCredentialsFactory(selectShift, shiftInitByID, { ...options, defaultValue: null });
export const [useClientShiftsInRange] = dataHookWithCredentialsFactory(selectClientShiftsInRange, queryMapFactory(fetchClientShiftsInRange, 'id', shiftInitByID, listen), options);

export const [useReadClientShift] = readonlyHookWithCredentialsFactory(selectShift, id => shiftInitByID, optionsSingle);
export const [useReadClientShifts] = readonlyHookWithCredentialsFactory(selectClientShifts, ids => shiftInitByID, options);


const reducer = { name: shiftClientSlice.name, reducer: shiftClientSlice.reducer }
export default reducer;